import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ApiDocsView } from "../features/api-docs/components/index.ts";
import { ApiStatusView } from "../features/api-status/components/index.ts";
import { AuditLogView } from "../features/audit-log/components/index.ts";
import { DiscoveryView } from "../features/discovery/components/index.ts";
import { EvWorkstationView } from "../features/ev-workstation/components/index.ts";
import { IntelligenceView } from "../features/intelligence/index.ts";
import {
  submitProject as submitProjectRequest,
  validateProjectHandle,
} from "../features/leaderboard/api/leaderboard-api.ts";
import { LeaderboardView } from "../features/leaderboard/components/index.ts";
import type { Project } from "../features/leaderboard/types/index.ts";
import { MethodologyView } from "../features/methodology/components/index.ts";
import { ProjectDetailModal, RescoreModal } from "../features/project-detail/components/index.ts";
import { TrendsView } from "../features/trends/components/index.ts";
// useTrendsSummaryQuery removed — TrendsView now manages its own data
import {
  ProjectSubmitForm,
  ScoringProgressPanel,
  ToastContainer,
} from "../shared/components/index.ts";
import { useScoringPolling } from "../shared/hooks/use-scoring-polling.ts";
import { apiRequest } from "../shared/lib/api-client.ts";
import { queryClient } from "../shared/lib/query-client.ts";
import { queryKeys } from "../shared/lib/query-keys.ts";
import { PROVIDER_NAMES } from "../shared/lib/scoring-utils.ts";
import {
  type ProjectSubmitQueue,
  parseCommunityStatus,
  type ScoringProgress,
  type Toast,
  type ValidationResult,
} from "../shared/types/scoring.ts";
import { AdminDashboardView } from "./components/AdminDashboardView.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { LoginView } from "./components/LoginView.tsx";
import { PasswordResetView } from "./components/PasswordResetView.tsx";
import { AuthProvider, useAuth } from "./contexts/AuthContext.tsx";
import { reportClientError } from "./lib/error-reporter.ts";
import "./styles.css";

function App() {
  const appQueryClient = useQueryClient();
  const { user, role, isLoading, authError, logout } = useAuth();
  const isAdmin = role === "admin";
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const [handle, setHandle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [selectedProject, setSelectedProject] = useState<{
    project: Project;
    workflowMode: boolean;
    detailView: "full" | "call_prep" | "operating_summary";
  } | null>(null);
  const [activeView, setActiveView] = useState<
    | "leaderboard"
    | "trends"
    | "methodology"
    | "api-docs"
    | "api-status"
    | "discovery"
    | "ev-workstation"
    | "audit-log"
    | "intelligence"
    | "admin"
  >("leaderboard");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [scoringProgressMap, setScoringProgressMap] = useState<Record<string, ScoringProgress>>({});
  const [showRescoreModal, setShowRescoreModal] = useState(false);
  const [founderPrompt, setFounderPrompt] = useState<
    Record<string, { name: string; twitter: string; submitting: boolean }>
  >({});
  const [workstationFocusRequest, setWorkstationFocusRequest] = useState<{
    project: Project;
    nonce: number;
    warmContactKey?: string;
  } | null>(null);

  const showSubmitTools =
    activeView !== "admin" &&
    activeView !== "audit-log" &&
    activeView !== "ev-workstation" &&
    activeView !== "methodology" &&
    activeView !== "api-docs" &&
    activeView !== "intelligence";
  const useWideContentShell =
    activeView === "leaderboard" ||
    activeView === "methodology" ||
    activeView === "api-docs" ||
    activeView === "api-status" ||
    activeView === "discovery" ||
    activeView === "ev-workstation" ||
    activeView === "intelligence" ||
    activeView === "admin";

  // Toast management
  const addToast = useCallback((type: Toast["type"], title: string, message: string) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, type, title, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Scoring polling
  useScoringPolling({
    scoringProgressMap,
    setScoringProgressMap,
    selectedProjectId: selectedProject?.project.id ?? null,
    addToast,
  });

  // Clear admin-gated queries for non-admin users.
  useEffect(() => {
    if (isAdmin) return;
    appQueryClient.removeQueries({ queryKey: queryKeys.apiStatus.summary() });
    appQueryClient.removeQueries({ queryKey: queryKeys.evWorkstation.projectsRoot() });
    appQueryClient.removeQueries({ queryKey: ["ev-workstation", "call-prep"] });
    appQueryClient.removeQueries({ queryKey: ["ev-workstation", "call-prep-status"] });
    appQueryClient.removeQueries({ queryKey: queryKeys.evWorkstation.followUps() });
    appQueryClient.removeQueries({ queryKey: queryKeys.evWorkstation.summary() });
    appQueryClient.removeQueries({ queryKey: queryKeys.evWorkstation.brief() });
    appQueryClient.removeQueries({ queryKey: queryKeys.evWorkstation.owners() });
  }, [isAdmin, appQueryClient]);

  function handleInputChange(value: string) {
    setHandle(value);
    setValidation(null);
  }

  async function submitProject(e: React.FormEvent) {
    e.preventDefault();
    if (!handle.trim() || submitting) return;

    setSubmitting(true);

    try {
      let currentValidation = validation;
      if (!currentValidation) {
        setValidating(true);
        currentValidation = (await validateProjectHandle(handle.trim())) as ValidationResult;
        setValidation(currentValidation);
        setValidating(false);

        if (currentValidation.existing) {
          setSubmitting(false);
          return;
        }
      }

      const cleanHandle =
        currentValidation?.handle || handle.trim().replace(/^@/, "").toLowerCase();

      const submitBody: Record<string, unknown> = { twitterHandle: cleanHandle };
      if (currentValidation?.moniData) {
        submitBody.moniData = currentValidation.moniData;
      }
      const data = (await submitProjectRequest(
        submitBody as { twitterHandle: string; moniData?: unknown }
      )) as {
        data?: { id: string };
        audit?: ProjectSubmitQueue;
        community?: ProjectSubmitQueue;
      };

      const createdProject = data.data;
      if (createdProject) {
        setHandle("");
        setValidation(null);
        const auditRaw = data.audit;
        const communityRaw = data.community;
        const now = Date.now();
        const usingCachedHourlyRun =
          auditRaw?.status === "already_queued" || auditRaw?.isNew === false;
        const communityStatus =
          communityRaw?.status === "skipped"
            ? parseCommunityStatus(undefined, "skipped")
            : parseCommunityStatus(
                {
                  status: "queued",
                  queueState: null,
                  queuePosition: null,
                  waitingMs: null,
                  jobId: communityRaw?.jobId ?? null,
                  progress: null,
                  latestReport: null,
                },
                "queued"
              );

        setScoringProgressMap((prev) => ({
          ...prev,
          [createdProject.id]: {
            projectId: createdProject.id,
            handle: cleanHandle,
            status: "queued",
            startedAt: now,
            updatedAt: now,
            lastPolledAt: 0,
            jobId: auditRaw?.jobId ?? null,
            queueState: "waiting",
            queuePosition: null,
            waitingMs: 0,
            usingCachedHourlyRun,
            completedProviders: [],
            failedProviders: [],
            totalProviders: Object.keys(PROVIDER_NAMES).length,
            community: communityStatus,
          },
        }));

        void appQueryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.projectsRoot() });

        if (usingCachedHourlyRun) {
          addToast(
            "info",
            "Using Cached Hourly Run",
            `@${cleanHandle} already has an hourly job. Tracking the existing run.`
          );
        } else {
          addToast("info", "Scoring started", `Processing @${cleanHandle}...`);
        }
      }
    } catch (err) {
      setValidating(false);
      console.error("Failed to submit project:", err);
      reportClientError({ scope: "App:submitProject", error: err });
      addToast(
        "error",
        "Submission failed",
        err instanceof Error ? err.message : "Could not submit project for scoring"
      );
    } finally {
      setSubmitting(false);
    }
  }

  function openProjectDetail(
    project: Project,
    options?: { workflowMode?: boolean; detailView?: "full" | "call_prep" | "operating_summary" }
  ) {
    setSelectedProject({
      project,
      workflowMode: options?.workflowMode === true,
      detailView: options?.detailView ?? "full",
    });
  }

  function openEvWorkstationProjectDetail(
    project: Project,
    options?: { workflowMode?: boolean; detailView?: "full" | "call_prep" | "operating_summary" }
  ) {
    openProjectDetail(project, {
      workflowMode: true,
      detailView: options?.detailView,
    });
  }

  function closeModal() {
    setSelectedProject(null);
    setShowRescoreModal(false);
  }

  function handleNavigateToWorkstation(project: Project, warmContactKey?: string) {
    closeModal();
    setWorkstationFocusRequest({
      project,
      nonce: Date.now(),
      warmContactKey,
    });
    setActiveView("ev-workstation");
  }

  function handleDismissTask(projectId: string) {
    setScoringProgressMap((prev) => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }

  function handleFounderPromptChange(projectId: string, field: "name" | "twitter", value: string) {
    setFounderPrompt((prev) => ({
      ...prev,
      [projectId]: {
        name: field === "name" ? value : (prev[projectId]?.name ?? ""),
        twitter: field === "twitter" ? value : (prev[projectId]?.twitter ?? ""),
        submitting: false,
      },
    }));
  }

  async function submitFounderHint(projectId: string, handle: string) {
    if (!isAdmin) return;

    const prompt = founderPrompt[projectId];
    if (!prompt || (!prompt.name.trim() && !prompt.twitter.trim())) return;

    setFounderPrompt((prev) => {
      const cur = prev[projectId] ?? { name: "", twitter: "", submitting: false };
      return { ...prev, [projectId]: { ...cur, submitting: true } };
    });

    try {
      const res = await apiRequest(`/v1/projects/${projectId}/pedigree-rescore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          founderName: prompt.name.trim() || undefined,
          founderTwitter: prompt.twitter.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        const errObj = errBody.error as Record<string, unknown> | undefined;
        throw new Error(
          typeof errObj?.message === "string"
            ? errObj.message
            : typeof errBody.message === "string"
              ? errBody.message
              : "Rescore failed"
        );
      }

      const payload = (await res.json()) as {
        data: {
          score?: number;
          confidence?: number;
          founder?: ScoringProgress["founder"];
        };
      };
      const payloadData = payload.data;

      setScoringProgressMap((prev) => {
        const current = prev[projectId];
        if (!current) return prev;
        return {
          ...prev,
          [projectId]: {
            ...current,
            score: payloadData.score ?? current.score,
            confidence: payloadData.confidence ?? current.confidence,
            founder: payloadData.founder ?? current.founder,
          },
        };
      });

      setFounderPrompt((prev) => {
        const next = { ...prev };
        delete next[projectId];
        return next;
      });

      addToast("success", "Pedigree rescored", `Founder info updated for @${handle}`);

      void appQueryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.projectsRoot() });
    } catch (err) {
      addToast(
        "error",
        "Rescore failed",
        err instanceof Error ? err.message : "Could not rescore pedigree"
      );
      setFounderPrompt((prev) => {
        const cur = prev[projectId] ?? { name: "", twitter: "", submitting: false };
        return { ...prev, [projectId]: { ...cur, submitting: false } };
      });
    }
  }

  function handleRescoreSubmitted(projectId: string, progress: ScoringProgress) {
    setScoringProgressMap((prev) => ({ ...prev, [projectId]: progress }));
  }

  useEffect(() => {
    const isAllowedForRole =
      activeView === "leaderboard" ||
      activeView === "trends" ||
      activeView === "methodology" ||
      activeView === "api-docs" ||
      activeView === "audit-log" ||
      activeView === "intelligence" ||
      (isAdmin &&
        (activeView === "api-status" ||
          activeView === "discovery" ||
          activeView === "ev-workstation" ||
          activeView === "admin"));

    if (isAllowedForRole) return;
    setActiveView("leaderboard");
  }, [activeView, isAdmin]);

  if (pathname === "/reset-password") {
    return <PasswordResetView />;
  }

  if (isLoading) {
    return (
      <div className="login-container">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Checking session</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginView authError={authError} />;
  }

  // Standalone API docs kiosk mode — used by the shareable /api-docs URL.
  // Renders only the docs viewer with a minimal header, no nav, no submit
  // tools. Auth still required (via the user check above).
  //
  // The .header is position:fixed (z-index 100), so the content wrapper
  // must add top padding equal to the header height (~112px / 7rem) to
  // prevent the header from overlapping the docs.
  if (pathname === "/api-docs") {
    return (
      <>
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
        <header className="header">
          <div className="logo">
            <div className="logo-mark">
              <span />
              <span />
              <span />
            </div>
            Legion Scout — API Reference
          </div>
          <div className="header-actions">
            <div className="header-user">
              <span className="header-user-email">{user.email ?? "unknown"}</span>
              <span className="header-user-role">{isAdmin ? "admin" : "viewer"}</span>
              <button
                type="button"
                className="btn btn-small header-logout-btn"
                onClick={() => {
                  void logout();
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </header>
        <main
          style={{
            paddingTop: "5rem",
            paddingLeft: "1.25rem",
            paddingRight: "1.25rem",
            paddingBottom: "1.25rem",
            minHeight: "100vh",
            boxSizing: "border-box",
          }}
        >
          <ApiDocsView />
        </main>
      </>
    );
  }

  const viewHeadline =
    activeView === "admin"
      ? {
          titlePrimary: "Access",
          accent: "Control",
          titleSecondary: "Dashboard",
          subtitle: "Provision users, assign roles, and dispatch password reset invites.",
        }
      : activeView === "discovery"
        ? {
            titlePrimary: "New Project",
            accent: "Incoming",
            titleSecondary: "Tracker",
            subtitle:
              "Live discovery pipeline status with ETA, current throughput, and incoming project feed.",
          }
        : activeView === "ev-workstation"
          ? {
              titlePrimary: "Ev's",
              accent: "Workstation",
              titleSecondary: "Pipeline",
              subtitle:
                "Admin-only BD workflow operations for qualification, follow-ups, ownership, and onboarding handoff state.",
            }
          : activeView === "api-status"
            ? {
                titlePrimary: "System",
                accent: "API",
                titleSecondary: "Status",
                subtitle:
                  "Provider health, API key configuration, and audit run statistics at a glance.",
              }
            : activeView === "trends"
              ? {
                  titlePrimary: "Alpha",
                  accent: "Trend",
                  titleSecondary: "Radar",
                  subtitle:
                    "Momentum and category movement over time for tracked projects across the leaderboard.",
                }
              : activeView === "methodology"
                ? {
                    titlePrimary: "Scoring",
                    accent: "Methodology",
                    titleSecondary: "& Discovery",
                    subtitle:
                      "How the Alpha Score is calculated, what each category measures, and how new projects are discovered.",
                  }
                : activeView === "api-docs"
                  ? {
                      titlePrimary: "Viewer",
                      accent: "API",
                      titleSecondary: "Reference",
                      subtitle:
                        "Read-only programmatic access to scores, leaderboard, project details, and trend analytics. Authenticate with your viewer API key.",
                    }
                  : activeView === "audit-log"
                    ? {
                        titlePrimary: "Audit",
                        accent: "Run",
                        titleSecondary: "Log",
                        subtitle:
                          "Chronological feed of every audit run, manual rescore, and TGE proximity scan across the corpus.",
                      }
                    : activeView === "intelligence"
                      ? {
                          titlePrimary: "Portfolio",
                          accent: "Intelligence",
                          titleSecondary: "Aggregates",
                          subtitle:
                            "Cross-project analytics: backer leagues, co-investor graphs, ecosystem snapshots, stealth watchlist, and stage mix.",
                        }
                      : {
                          titlePrimary: "Alpha",
                          accent: "Score",
                          titleSecondary: "Leaderboard",
                          subtitle:
                            "Real-time intelligence scoring for crypto projects. Multi-signal analysis across social, backers, pedigree, and development metrics.",
                        };

  return (
    <>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      <header className="header">
        <div className="logo">
          <div className="logo-mark">
            <span />
            <span />
            <span />
          </div>
          Legion Scout
        </div>
        <div className="header-actions">
          <div className="header-status">
            <div className="status-indicator" />
            Live
          </div>
          <div className="header-user">
            <span className="header-user-email">{user.email ?? "unknown"}</span>
            <span className="header-user-role">{isAdmin ? "admin" : "basic"}</span>
            <button
              type="button"
              className="btn btn-small header-logout-btn"
              onClick={() => {
                void logout();
              }}
            >
              Log Out
            </button>
          </div>
        </div>
      </header>

      <div className="app-shell">
        <div className="app-shell__intro">
          <div className="hero-section">
            <h1 className="hero-title">
              {viewHeadline.titlePrimary} <span className="accent">{viewHeadline.accent}</span>
              <br />
              {viewHeadline.titleSecondary}
            </h1>
            <p className="hero-subtitle">{viewHeadline.subtitle}</p>
          </div>

          {showSubmitTools && (
            <>
              <ProjectSubmitForm
                handle={handle}
                onHandleChange={handleInputChange}
                validation={validation}
                validating={validating}
                submitting={submitting}
                onSubmit={submitProject}
              />
              <ScoringProgressPanel
                scoringProgressMap={scoringProgressMap}
                onDismissTask={handleDismissTask}
                founderPrompt={founderPrompt}
                onFounderPromptChange={handleFounderPromptChange}
                onSubmitFounderHint={submitFounderHint}
                canSubmitFounderHint={isAdmin}
              />
            </>
          )}

          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${activeView === "leaderboard" ? "active" : ""}`}
              onClick={() => setActiveView("leaderboard")}
            >
              Leaderboard
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${activeView === "trends" ? "active" : ""}`}
              onClick={() => setActiveView("trends")}
            >
              Trends
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${activeView === "methodology" ? "active" : ""}`}
              onClick={() => setActiveView("methodology")}
            >
              How It Works
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${activeView === "audit-log" ? "active" : ""}`}
              onClick={() => setActiveView("audit-log")}
            >
              Audit Log
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${activeView === "intelligence" ? "active" : ""}`}
              onClick={() => setActiveView("intelligence")}
            >
              Intelligence
            </button>
            {isAdmin && (
              <>
                <button
                  type="button"
                  className={`view-toggle-btn ${activeView === "api-status" ? "active" : ""}`}
                  onClick={() => setActiveView("api-status")}
                >
                  API Status
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${activeView === "discovery" ? "active" : ""}`}
                  onClick={() => setActiveView("discovery")}
                >
                  Discovery Status
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${activeView === "ev-workstation" ? "active" : ""}`}
                  onClick={() => setActiveView("ev-workstation")}
                >
                  Ev's Workstation
                </button>
                <button
                  type="button"
                  className={`view-toggle-btn ${activeView === "admin" ? "active" : ""}`}
                  onClick={() => setActiveView("admin")}
                >
                  Admin
                </button>
              </>
            )}
          </div>
        </div>

        <div
          className={[
            "app-shell__content",
            useWideContentShell ? "app-shell__content--wide" : "",
            activeView === "ev-workstation" ? "app-shell__content--workstation" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {activeView === "leaderboard" && (
            <LeaderboardView onOpenProjectDetail={openProjectDetail} />
          )}
          {activeView === "trends" && <TrendsView onOpenProjectDetail={openProjectDetail} />}
          {activeView === "methodology" && <MethodologyView />}
          {activeView === "api-docs" && <ApiDocsView />}
          {activeView === "api-status" && isAdmin && <ApiStatusView />}
          {activeView === "discovery" && isAdmin && (
            <DiscoveryView onOpenProjectDetail={openProjectDetail} />
          )}
          {activeView === "ev-workstation" && isAdmin && (
            <EvWorkstationView
              onOpenProjectDetail={openEvWorkstationProjectDetail}
              focusRequest={workstationFocusRequest}
              onFocusApplied={() => setWorkstationFocusRequest(null)}
              addToast={addToast}
            />
          )}
          {activeView === "audit-log" && <AuditLogView canSeeActors={isAdmin} />}
          {activeView === "intelligence" && (
            <IntelligenceView onOpenProjectDetail={openProjectDetail} />
          )}
          {activeView === "admin" && isAdmin && <AdminDashboardView addToast={addToast} />}
        </div>
      </div>

      {selectedProject && (
        <ProjectDetailModal
          project={selectedProject.project}
          onClose={closeModal}
          onOpenRescoreModal={() => setShowRescoreModal(true)}
          scoringProgress={scoringProgressMap[selectedProject.project.id]}
          canManageFounder={isAdmin}
          workflowMode={selectedProject.workflowMode}
          detailView={selectedProject.detailView}
          onNavigateToWorkstation={handleNavigateToWorkstation}
          onOpenProjectDetail={openProjectDetail}
          onFocusWarmContact={
            selectedProject.workflowMode
              ? (_projectId, warmContactKey) => {
                  setWorkstationFocusRequest({
                    project: selectedProject.project,
                    nonce: Date.now(),
                    warmContactKey,
                  });
                }
              : undefined
          }
        />
      )}

      {showRescoreModal && selectedProject && (
        <RescoreModal
          project={selectedProject.project}
          onClose={() => setShowRescoreModal(false)}
          onRescoreSubmitted={handleRescoreSubmitted}
          addToast={addToast}
        />
      )}
    </>
  );
}

const rootElement = document.getElementById("root");
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
